<script setup lang="ts">
import { ButtonComponent, FlexComponent, TypographyComponent } from '@metapic-apps/shared'
import { ElIcon, ElOption, ElSelect } from 'element-plus'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'

import { useGetUserTags, useSetUserTagsOnUser } from '@/api/user-tags.api'
import EditPenIcon from '@/assets/icons/edit-pen.svg?component'
import { useAdvertiserStore } from '@/stores/advertiser.stores'

const store = useStore()
const advertiserStore = useAdvertiserStore()
const advertiserId = computed<number | undefined>(() => advertiserStore.advertiser?.id)

const props = defineProps<{
  influencerTags: string[]
  influencerId: number
}>()

const isEditing = ref(false)
const chosenTagsInEditing = ref<string[]>([])

const gatedAdvertiserId = computed(() => (isEditing.value ? advertiserId.value : undefined))
const { data: userTags } = useGetUserTags(gatedAdvertiserId)
const { setUserTagsOnUser, loading: isSaving } = useSetUserTagsOnUser(advertiserId)

const startEditing = () => {
  chosenTagsInEditing.value = [...props.influencerTags]
  isEditing.value = true
}

const saveTags = async () => {
  const tagsToSave =
    chosenTagsInEditing.value.length > 0
      ? chosenTagsInEditing.value.map((name) => {
          const existingTag = userTags.value.find((tag) => tag.name === name)
          return existingTag ? { name: existingTag.name, id: existingTag.id } : { name }
        })
      : []

  const updatedTags = await setUserTagsOnUser(props.influencerId, tagsToSave)
  if (updatedTags) {
    await store.dispatch(
      'influencer/setTagsOnInfluencer',
      updatedTags.map((tag) => tag.name),
    )
  }

  chosenTagsInEditing.value = []
  isEditing.value = false
}
</script>
<template>
  <template v-if="!isEditing">
    <TypographyComponent variant="body-medium">
      {{ props.influencerTags.length > 0 ? props.influencerTags.join(', ') : 'No tags have been set.' }}
    </TypographyComponent>
    <ButtonComponent variant="text" class="influencer-tags__edit-button" @click="startEditing">
      <template #iconLeft>
        <ElIcon :size="18" color="var(--inverted)">
          <EditPenIcon />
        </ElIcon>
      </template>
      Edit
    </ButtonComponent>
  </template>
  <template v-else>
    <FlexComponent align="center" :gap="8">
      <ElSelect
        v-model="chosenTagsInEditing"
        multiple
        filterable
        allow-create
        default-first-option
        :reserve-keyword="false"
        collapse-tags
        placeholder="Select or Create"
        :teleported="false"
      >
        <ElOption v-for="tag in userTags" :key="tag.id" :label="tag.name" :value="tag.name" />
      </ElSelect>
      <ButtonComponent round variant="accent" :is-loading="isSaving" @click="saveTags">Save</ButtonComponent>
    </FlexComponent>
  </template>
</template>
<style scoped lang="scss">
.button.influencer-tags__edit-button {
  padding: 8px 16px;
}

:deep(.el-tag) {
  padding: 4px 8px;
  border-radius: 24px;
  font-weight: 600;
  line-height: 16px;
  height: 24px;
}

:deep(.el-select__wrapper) {
  --el-border-radius-base: 8px;
  height: 40px;
  padding: 8px 16px;
}
</style>
